// ---- Variant resolution + seeded variation ----
// The pure half of the variation system (THREE-free, manifest-free — imported by
// the model registry, the loader, validation, and tests):
//   resolveModelDoc  — flatten a variant doc (base + overrides) into one ModelDoc.
//                      Merge rules: palette SHALLOW-merge, body DEEP-merge, parts
//                      APPEND (base's first), slots SHALLOW-merge, variation =
//                      child's else base's. Single-level chains only (a base may
//                      not itself have a base) — validation enforces, we assert.
//   variantSeed      — FNV-1a hash of a spawn/entry id → the deterministic seed
//                      every consumer shares (client, server, studio), because
//                      spawn ids already ride the wire. NO protocol change.
//   seededShade/... — tiny seed→knob helpers so build/dress layers agree.
//   scalePrims      — wrap a Prims sink so all emitted geometry is uniformly
//                     scaled: the build-time way to apply scale jitter WITHOUT
//                     touching group.scale (combat's flinch pop owns that) or
//                     the NPC spec's collider.

import type { Prims } from "../meshkit.ts";
import type { ModelDoc } from "./parts.ts";

/** FNV-1a 32-bit — the shared id→seed hash for per-instance variation. */
export function variantSeed(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministic 0..1 from a seed + lane (lane keeps knobs independent). */
export function seededUnit(seed: number, lane: number): number {
  let h = (seed ^ Math.imul(lane + 1, 0x9e3779b9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x21f0aaad);
  h = Math.imul(h ^ (h >>> 15), 0x735a2d97);
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 0xffffffff;
}

/** Deterministic −1..+1 (centered) from a seed + lane. */
export function seededSigned(seed: number, lane: number): number {
  return seededUnit(seed, lane) * 2 - 1;
}

// Knob lanes — one per variation channel so they don't correlate.
export const LANE_SHADE = 0;
export const LANE_SCALE = 1;
export const LANE_GAIT_PHASE = 2;
export const LANE_GAIT_SPEED = 3;

/** Nested-record merge (objects recurse, everything else replaces). Shared by
 *  variant `body` resolution and the wearable `wear.body` overlay (player.ts). */
export function deepMerge(
  base: Record<string, unknown>,
  over: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    const prev = out[k];
    if (
      v && typeof v === "object" && !Array.isArray(v) &&
      prev && typeof prev === "object" && !Array.isArray(prev)
    ) {
      out[k] = deepMerge(prev as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Flatten a (possibly variant) model doc against its base. `lookup` resolves a
 *  base id to its doc. Returns the doc itself when it has no base. Throws on a
 *  dangling base, archetype mismatch, or a chained base — validation reports
 *  all three before this can fire at runtime. */
export function resolveModelDoc(doc: ModelDoc, lookup: (id: string) => ModelDoc | undefined): ModelDoc {
  if (!doc.base) return doc;
  const base = lookup(doc.base);
  if (!base) throw new Error(`model "${doc.id}": unknown base "${doc.base}"`);
  if (base.base) throw new Error(`model "${doc.id}": base "${base.id}" is itself a variant (single-level chains only)`);
  if (base.archetype !== doc.archetype) {
    throw new Error(`model "${doc.id}": archetype "${doc.archetype}" ≠ base's "${base.archetype}"`);
  }
  // Empty sections are OMITTED, not emitted as {}/[] — a "parts"-archetype
  // variant (a re-skinned ground prop) must stay body-less/slot-less or the
  // merged re-parse in validate.ts rejects it.
  const body = deepMerge(base.body ?? {}, doc.body ?? {});
  const parts = [...(base.parts ?? []), ...(doc.parts ?? [])];
  const slots = { ...base.slots, ...doc.slots };
  // Landmarks shallow-merge so a bow variant inherits grip/string and can
  // override a single point if the mesh shifts.
  const landmarks = { ...base.landmarks, ...doc.landmarks };
  return {
    id: doc.id,
    archetype: doc.archetype,
    palette: { ...base.palette, ...doc.palette },
    ...(Object.keys(body).length ? { body } : {}),
    ...(parts.length ? { parts } : {}),
    ...(Object.keys(slots).length ? { slots } : {}),
    ...(Object.keys(landmarks).length ? { landmarks } : {}),
    variation: doc.variation ?? base.variation,
  };
}

/** Wrap a prim sink so every emitted coordinate/size is scaled by `k` — limb and
 *  socket pivots included, so the rig scales coherently. Identity at k=1. */
export function scalePrims(p: Prims, k: number): Prims {
  if (k === 1) return p;
  const v = (t: readonly [number, number, number]): [number, number, number] =>
    [t[0] * k, t[1] * k, t[2] * k];
  return {
    // rot is angular — pass through unchanged (only positions/sizes scale).
    box: (x, y, z, w, h, d, color, rot) => p.box(x * k, y * k, z * k, w * k, h * k, d * k, color, rot),
    blob: (x, y, z, r, color, rot) => p.blob(x * k, y * k, z * k, r * k, color, rot),
    cone: (x, baseY, z, r, h, color, rot) => p.cone(x * k, baseY * k, z * k, r * k, h * k, color, rot),
    cylinder: (x, baseY, z, rTop, rBottom, h, color, rot) =>
      p.cylinder(x * k, baseY * k, z * k, rTop * k, rBottom * k, h * k, color, rot),
    tube: (x, y, z, r, len, color, axis) => p.tube(x * k, y * k, z * k, r * k, len * k, color, axis),
    rod: (x0, y0, z0, x1, y1, z1, r, color) =>
      p.rod(x0 * k, y0 * k, z0 * k, x1 * k, y1 * k, z1 * k, r * k, color),
    // ball/arc/bone/lathe depth + bulge + sweep are fractional/angular shaping
    // knobs — pass through unscaled; only positions/sizes scale.
    ball: (x, y, z, r, color, depth, rot) => p.ball(x * k, y * k, z * k, r * k, color, depth, rot),
    arc: (x, y, z, r, tube, color, sweep, rot) =>
      p.arc(x * k, y * k, z * k, r * k, tube * k, color, sweep, rot),
    bone: (x0, y0, z0, x1, y1, z1, r0, r1, color, depth, bulge) =>
      p.bone(x0 * k, y0 * k, z0 * k, x1 * k, y1 * k, z1 * k, r0 * k, r1 * k, color, depth, bulge),
    lathe: (x, baseY, z, profile, color, depth, segs, rot, frontBulge) =>
      p.lathe(x * k, baseY * k, z * k, profile.map(([r, y]) => [r * k, y * k] as const), color, depth, segs, rot,
        frontBulge?.map(([y, dz]) => [y * k, dz * k] as const)),
    limb: (name, pivot, fn) => p.limb(name, v(pivot), fn),
    socket: (name, pivot) => p.socket(name, v(pivot)),
    inLimb: (name, fn) => p.inLimb(name, fn),
  };
}
